/**
 * Data Hub — Summarized tab.
 *
 * Same heatmap as Normalized, keyed by period rather than date, with the
 * reference app's By period ↔ By account toggle, granularity selector and
 * Expand/Collapse all.
 */
import { useState } from "react";

import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import { MetricMatrix, type MatrixValueMode } from "../components/MetricMatrix";
import { TabCard, TabError, TabLoading } from "../components/TabState";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

export function SummarizedTab() {
  const [dataset] = useActiveDataset();
  const [axis, setAxis] = useState<"period" | "account">("period");
  const [mode, setMode] = useState<MatrixValueMode>("normalized");
  const [grain, setGrain] = useState("monthly");
  const [expanded, setExpanded] = useState(false);
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getSummarizedMatrix({ axis, period_type: grain }),
    [dataset, axis, grain]
  );

  if (loading && !data) return <TabLoading label="Loading summaries…" />;
  if (error) return <TabError error={error} />;
  if (!data) return null;

  const rowNoun = axis === "account" ? "accounts" : `${grain} periods`;

  return (
    <TabCard
      title="Summary matrix"
      subtitle={`${data.metrics.length} metrics · ${data.rows.length} ${rowNoun}`}
      actions={
        <>
          <DropdownSelector
            label="View"
            selected={axis}
            options={[
              { value: "period", label: "By period" },
              { value: "account", label: "By account" },
            ]}
            onSelect={v => setAxis(v as "period" | "account")}
          />
          <DropdownSelector
            label="Granularity"
            selected={grain}
            options={[
              { value: "weekly", label: "Weekly" },
              { value: "monthly", label: "Monthly" },
              { value: "fiscal_quarter", label: "Fiscal quarter" },
              { value: "fiscal_year", label: "Fiscal year" },
            ]}
            onSelect={setGrain}
          />
          <DropdownSelector
            label="Values"
            selected={mode}
            options={[
              { value: "normalized", label: "Normalized 0–100" },
              { value: "actual", label: "Actual + UOM" },
            ]}
            onSelect={v => setMode(v as MatrixValueMode)}
          />
          <Button
            type="button"
            variant="outline-primary"
            onClick={() => setExpanded(e => !e)}
            className="shrink-0"
          >
            {expanded ? "Collapse all" : "Expand all"}
          </Button>
        </>
      }
    >
      <MetricMatrix
        matrix={data}
        mode={mode}
        rowHeaderLabel={axis === "account" ? "Account" : "Period"}
      />
      {expanded && (
        <p className="mt-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Expand all keeps every category column visible. Granularity is applied
          server-side from `account_metrics_period_summary`.
        </p>
      )}
    </TabCard>
  );
}

export default SummarizedTab;
